import { useCallback, useState } from "react"
import { Box } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import Card from "components/card"
import usePat from "../../hooks/use-pat"
import PatTokenRow, { TYPE_LABEL } from "./pat-token-row"
import PatStatePanel from "./state-panel"
import RotatePatDialog from "./rotate-pat-dialog"
import RevokePatDialog from "./revoke-pat-dialog"
import type { PatType, PersonalAccessToken } from "../../types"

const PAT_TYPES: PatType[] = ["read", "read_write"]

const CONTENT_SX = { px: 2 } satisfies SxProps<Theme>

export default function PersonalAccessTokens() {
  const { tokens, canCreateReadWrite, patEnabled, isPending, isError, isMutating, refetch, createToken, revealToken, rotateToken, revokeToken } = usePat()
  const [revealed, setRevealed] = useState<Record<string, string>>({})
  const [rotateTarget, setRotateTarget] = useState<PersonalAccessToken | null>(null)
  const [revokeTarget, setRevokeTarget] = useState<PersonalAccessToken | null>(null)

  const tokenByType = (type: PatType) => tokens.find(token => token.tokenType === type)

  const hideRevealed = useCallback((tokenId: string) => setRevealed(prev =>
    Object.fromEntries(Object.entries(prev).filter(([id]) => id !== tokenId))), [])

  const handleReveal = useCallback(async (token: PersonalAccessToken) => {
    if (revealed[token.id]) {
      hideRevealed(token.id)
      return
    }
    try {
      const { secret } = await revealToken(token.id)
      setRevealed(prev => ({ ...prev, [token.id]: secret }))
    } catch {
      // Notification is handled by the hook on error.
    }
  }, [revealed, hideRevealed, revealToken])

  const handleCreate = useCallback(async (type: PatType) => {
    try {
      const { token } = await createToken(type)
      setRevealed(prev => ({ ...prev, [token.id]: token.secret }))
    } catch {
      // Notification is handled by the hook on error.
    }
  }, [createToken])

  const handleRotateConfirm = useCallback(async () => {
    const target = rotateTarget
    setRotateTarget(null)
    if (!target) return
    try {
      const { token } = await rotateToken(target.id)
      setRevealed(prev => ({ ...prev, [token.id]: token.secret }))
    } catch {
      // Notification is handled by the hook on error.
    }
  }, [rotateTarget, rotateToken])

  const handleRevokeConfirm = useCallback(async () => {
    const target = revokeTarget
    setRevokeTarget(null)
    if (!target) return
    try {
      await revokeToken(target.id)
      hideRevealed(target.id)
    } catch {
      // Notification is handled by the hook on error.
    }
  }, [revokeTarget, revokeToken, hideRevealed])

  // Until there is a response, `usePat()` defaults `patEnabled` to `true` so an eventual
  // error still renders the card (see use-pat.ts). That means `patEnabled` alone can't
  // decide visibility before the query settles — hide the card outright while pending so
  // disabled accounts never see it flash, and only consult `patEnabled` once it has.
  // `isPending` also covers the not-yet-started case (no account), which `isLoading` does
  // not; it goes false on error, so a failed fetch still reaches the error panel below.
  if (isPending) {
    return null
  }

  if (!patEnabled) {
    return null
  }

  return <Card
    title="Personal Access Tokens"
    className="personal-access-tokens"
    rightOptions={null}
    leftOptions={null}
    subHeader={null}
    fixed={false}
    noHeader={false}
    noSubHeaderPadding={false}
  >
    <Box className="content" sx={CONTENT_SX}>
      <PatStatePanel isError={isError} onRetry={refetch} />

      {!isError && PAT_TYPES.map(type => {
        const token = tokenByType(type)
        return <PatTokenRow
          key={type}
          type={type}
          token={token}
          canCreate={type === "read" || canCreateReadWrite}
          revealedSecret={token ? revealed[token.id] : undefined}
          disabled={isMutating}
          onCreate={handleCreate}
          onReveal={handleReveal}
          onRotate={setRotateTarget}
          onRevoke={setRevokeTarget}
        />
      })}
    </Box>

    <RotatePatDialog
      open={!!rotateTarget}
      tokenLabel={rotateTarget ? TYPE_LABEL[rotateTarget.tokenType] : ""}
      onClose={() => setRotateTarget(null)}
      onConfirm={handleRotateConfirm}
    />

    <RevokePatDialog
      open={!!revokeTarget}
      tokenLabel={revokeTarget ? TYPE_LABEL[revokeTarget.tokenType] : ""}
      onClose={() => setRevokeTarget(null)}
      onConfirm={handleRevokeConfirm}
    />
  </Card>
}
